/**
 * MetricDistributionRowContainer - Logic for distribution row with dropdown
 *
 * Owns the dropdown open-state and `useDropdownPositioning` wiring for the
 * shell-hosted selector (SE-5568 — migrated off the gluestack Menu, whose
 * Android positioning depended on the RN Modal + MenuScrollRoot workaround).
 * Select handlers close the panel and select in the same press; the SE-3823
 * setTimeout deferral is gone with the RN Modal it worked around.
 *
 * On mobile screens, distribution data above 30 dots is down-sampled with
 * even spacing to preserve the distribution shape while reducing rendering
 * cost in the Data Hub scroll view.
 * @remarks This is a Container component - handles business logic
 */

import { ChevronDown } from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";

import MetricDistributionRowView from "./MetricDistributionRowView";

import {
  FixedPositionDropdownShellTrigger,
  type FixedPositionDropdownShellTriggerRef,
  useDropdownPositioning,
} from "@/components/atoms/FixedPositionDropdownShell";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { METRIC_DROPDOWN_POSITIONING } from "@/features/player-detail/utils/metricShellDropdown";
import type { ShellDropdownWiring } from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";

import {
  calculateBeeSwarmPositions,
  type BeeSwarmInput,
} from "@/features/player-detail/utils/beeSwarmPositioning";
import type { MetricOption } from "@/features/player-detail/types";
import useMobileScreen from "@/hooks/useMobileScreen";
import { calculateNiceMaxValue } from "@/utils/radarChartUtils";

/**
 * Maximum dots rendered on mobile before down-sampling kicks in.
 * Keeps the bee swarm algorithm fast and reduces GPU overdraw during scroll.
 */
const MAX_MOBILE_DOTS = 30;

/**
 * Metric data for distribution charts with player info
 */
interface MetricData {
  readonly label: string;
  readonly value: number | null;
  readonly distributionData: readonly BeeSwarmInput[];
}

/**
 * Props for MetricDistributionRowContainer component
 */
interface MetricDistributionRowContainerProps {
  /** Metric data */
  readonly metric: MetricData;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Row index for indexed selection */
  readonly index: number;
  /** Available metrics for dropdown selection */
  readonly availableMetrics?: readonly MetricOption[];
  /** Handler for selecting a new metric at index */
  readonly onSelectMetricAtIndex?: (
    index: number,
    metric: MetricOption
  ) => void;
  /** Optional comparison player value (shows orange dot when provided) */
  readonly comparisonValue?: number | null;
  /** Reduces dot row height for mobile carousel (h-14 instead of h-20) */
  readonly compact?: boolean;
}

/**
 * Pill/dropdown trigger class (static — semantic tokens auto-invert by theme)
 */
const PILL_CLASSES =
  "flex-row items-center gap-2 rounded-md px-3 py-2 bg-surface-raised";

/**
 * Trigger label text class (static)
 */
const TEXT_CLASS = "text-content-primary";

/**
 * Down-sample distribution data to a target count using even spacing.
 * Sorts by value first so the sampled subset covers the full range and preserves
 * the overall distribution shape. Uses `(length - 1) / (targetCount - 1)` step
 * with `Math.round` so both the lowest and highest values are always included —
 * the previous `length / targetCount` formula could skip the last few items
 * (e.g. items 97-99 out of 100 when sampling to 30).
 * @param data - Full distribution data array
 * @param targetCount - Number of representative samples to return
 * @returns Evenly-spaced subset that represents the full distribution
 */
const sampleDistributionData = (
  data: readonly BeeSwarmInput[],
  targetCount: number
): readonly BeeSwarmInput[] => {
  const sorted = [...data].sort((a, b) => a.value - b.value);
  if (targetCount >= sorted.length) return sorted;
  if (targetCount <= 1) return sorted.slice(0, 1);
  const step = (sorted.length - 1) / (targetCount - 1);
  return Array.from(
    { length: targetCount },
    (_, i) => sorted[Math.round(i * step)]
  );
};

/**
 * MetricDistributionRowContainer provides menu trigger and handlers
 * @param props - Container props
 * @param props.metric - Metric data
 * @param props.isDark - Whether dark mode is active
 * @param props.index - Row index for selection handler
 * @param props.availableMetrics - Available metrics for dropdown
 * @param props.onSelectMetricAtIndex - Handler for metric selection with index
 * @param props.comparisonValue - Optional comparison player value
 * @param props.compact - Reduces dot row height for mobile carousel
 * @returns MetricDistributionRowView with computed props
 */
const MetricDistributionRowContainer = ({
  metric,
  isDark,
  index,
  availableMetrics = [],
  onSelectMetricAtIndex,
  comparisonValue,
  compact,
}: MetricDistributionRowContainerProps) => {
  const isSmallScreen = useMobileScreen();

  // On mobile, down-sample large distributions to reduce bee swarm computation and render cost
  const effectiveDistributionData = useMemo(
    () =>
      isSmallScreen && metric.distributionData.length > MAX_MOBILE_DOTS
        ? sampleDistributionData(metric.distributionData, MAX_MOBILE_DOTS)
        : metric.distributionData,
    [isSmallScreen, metric.distributionData]
  );

  // Compute dynamic max from all data points in this row
  const maxValue = useMemo(() => {
    const allValues = [
      ...(metric.value != null ? [metric.value] : []),
      ...metric.distributionData.map(d => d.value),
      ...(comparisonValue != null ? [comparisonValue] : []),
    ];
    const dataMax = allValues.length > 0 ? Math.max(...allValues, 0) : 100;
    return calculateNiceMaxValue(dataMax);
  }, [metric.value, metric.distributionData, comparisonValue]);

  // Calculate bee swarm positions to prevent dot overlap
  const swarmPositions = useMemo(
    () => calculateBeeSwarmPositions(effectiveDistributionData, { maxValue }),
    [effectiveDistributionData, maxValue]
  );

  // Shell open-state + positioning (SE-5568)
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => setIsOpen(prev => !prev), []);
  const { triggerRef, panelGeometry, shouldShow } = useDropdownPositioning({
    isOpen,
    ...METRIC_DROPDOWN_POSITIONING,
  });
  const dropdown = useMemo<ShellDropdownWiring>(
    () => ({ isOpen, onToggle, triggerRef, panelGeometry, shouldShow }),
    [isOpen, onToggle, triggerRef, panelGeometry, shouldShow]
  );

  // Shell-hosted trigger (replaces the gluestack Menu trigger render prop)
  const trigger = useMemo(
    () => (
      <FixedPositionDropdownShellTrigger
        ref={
          triggerRef as React.RefObject<FixedPositionDropdownShellTriggerRef>
        }
        isOpen={isOpen}
        onPress={onToggle}
        accessibilityLabel={`Select metric, currently ${metric.label}`}
      >
        <Stack direction="horizontal" className={PILL_CLASSES}>
          <Text variant="body-strong" className={TEXT_CLASS}>
            {metric.label}
          </Text>
          <Text variant="body-bold" className="text-needs-semantic-primary-400">
            {metric.value != null ? metric.value : "--"}
          </Text>
          <Icon as={ChevronDown} size="xs" className="text-content-muted" />
        </Stack>
      </FixedPositionDropdownShellTrigger>
    ),
    [isOpen, onToggle, metric.label, metric.value, triggerRef]
  );

  // Factory for stable select handlers — closes the panel and selects.
  const createSelectHandler = useCallback(
    (selectedMetric: MetricOption) => () => {
      setIsOpen(false);
      onSelectMetricAtIndex?.(index, selectedMetric);
    },
    [index, onSelectMetricAtIndex]
  );

  return (
    <MetricDistributionRowView
      metric={metric}
      isDark={isDark}
      availableMetrics={availableMetrics}
      dropdown={dropdown}
      trigger={trigger}
      createSelectHandler={createSelectHandler}
      comparisonValue={comparisonValue}
      swarmPositions={swarmPositions}
      maxValue={maxValue}
      compact={compact}
    />
  );
};

MetricDistributionRowContainer.displayName = "MetricDistributionRowContainer";

export default React.memo(MetricDistributionRowContainer);
